import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-50">
      {/* Header */}
      <div className="bg-white border-b border-rose-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Refund Policy</h1>
          <p className="text-gray-600 mt-2">Understanding our refund and cancellation terms</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Card className="p-8 mb-6">
          <div className="prose prose-rose max-w-none">
            <div className="bg-rose-50 p-6 rounded-lg border-2 border-rose-200 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mt-0 flex items-center">
                <CreditCard className="w-6 h-6 mr-2 text-rose-500" />
                7-Day Free Trial - No Risk
              </h2>
              <p className="text-gray-700 mb-0">
                We offer a 7-day free trial of our Growth Plan (normally $17/month) 
                for new subscribers to reduce risk and ensure you love your experience with Constant Love.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Trial Period Details</h2>
              <ul className="space-y-3 text-gray-700">
                <li><strong>Duration:</strong> 7 days from the moment you sign up</li>
                <li><strong>Full Access:</strong> Experience all Growth Plan features during your trial</li>
                <li><strong>No Charges:</strong> Your card won't be charged until the trial ends</li>
                <li><strong>Cancel Anytime:</strong> Cancel before the trial ends and pay nothing</li>
                <li><strong>Automatic Billing:</strong> If you don't cancel, you'll be charged $17/month after the trial</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Post-Trial Subscription</h2>
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">After Your Trial Ends:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li><strong>Immediate Access:</strong> You receive immediate access to all Constant Love features upon payment</li>
                  <li><strong>Digital Product:</strong> As a digital service, all content and features are available instantly</li>
                  <li><strong>Monthly Billing:</strong> You'll be charged $17/month on the same day each month</li>
                  <li><strong>Continuous Service:</strong> Your subscription continues until you cancel</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <RefreshCw className="w-6 h-6 mr-2 text-green-500" />
                Refund Eligibility
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">✅ Eligible for Refund:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Technical issues preventing access to core features</li>
                    <li>• Accidental duplicate charges</li>
                    <li>• Billing errors (charged wrong amount)</li>
                    <li>• First-time subscribers within 48 hours of initial charge (after trial)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">❌ Not Eligible for Refund:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Changed your mind after using the service</li>
                    <li>• Subscriptions beyond the current billing period</li>
                    <li>• Partial month refunds (we don't prorate)</li>
                    <li>• Previous billing periods already paid</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cancellation Process</h2>
              <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Cancel Your Subscription:</h3>
                <ol className="space-y-3 text-gray-700 list-decimal list-inside">
                  <li>Log into your Constant Love account</li>
                  <li>Go to Profile → Subscription & Billing</li>
                  <li>Click "Cancel Subscription"</li>
                  <li>Confirm your cancellation</li>
                  <li>You'll receive an email confirmation</li>
                </ol>
                <div className="mt-4 space-y-2">
                  <p className="font-semibold text-gray-900">What Happens After Cancellation:</p>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Access Until Period End:</strong> Continue using Constant Love until your current period expires</li>
                    <li><strong>No Future Charges:</strong> You won't be billed again</li>
                    <li><strong>Data Preserved:</strong> Your account data stays for 90 days if you want to return</li>
                    <li><strong>Downgrade to Free:</strong> You'll automatically move to the Reflect (free) plan</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Request a Refund</h2>
              <p className="text-gray-700 mb-4">
                If you believe you qualify for a refund based on our policy:
              </p>
              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <ol className="space-y-3 text-gray-700 list-decimal list-inside">
                  <li>Email <strong>support@constantloveco.com</strong></li>
                  <li>Include "Refund Request" in the subject line</li>
                  <li>Provide your account email and reason for the request</li>
                  <li>We'll respond within 2-3 business days</li>
                  <li>Approved refunds processed within 5-10 business days</li>
                </ol>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <AlertCircle className="w-6 h-6 mr-2 text-orange-500" />
                Important Notes
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li><strong>Processing Time:</strong> Refunds typically take 5-10 business days to appear in your account</li>
                <li><strong>Refund Method:</strong> Refunds are issued to the original payment method</li>
                <li><strong>Abuse Prevention:</strong> Repeated refund requests may result in account suspension</li>
                <li><strong>Questions?</strong> Contact support@constantloveco.com for any billing questions</li>
              </ul>
            </section>

            <div className="bg-rose-50 p-6 rounded-lg border-2 border-rose-200 mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 text-rose-500" />
                Our Commitment 💖
              </h3>
              <p className="text-gray-700">
                We want you to love your experience with Constant Love. If something isn't right, 
                we're here to help. Our goal is your growth and transformation, and we'll work with you 
                to resolve any issues fairly and quickly.
              </p>
            </div>

            <div className="text-center mt-8 text-sm text-gray-500">
              <p>Last updated: January 2025</p>
              <p>Questions? Email support@constantloveco.com</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RefundPolicy;